/**
 * Shared banker-journey UI sections:
 *   - <SimulatePushSection />  demo form that reproduces a banker's push
 *   - <PushHistorySection />   audit table of every journey created via push
 *
 * Used by both /journeys (main nav) and Administration → Bankers.
 */
import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Check, ExternalLink, RefreshCw, Copy } from "lucide-react";

export const SAMPLE_PAYLOAD = JSON.stringify({
  consumer: {
    full_name: "Nomsa Khumalo",
    email: "nomsa.khumalo@altara-preview.example.com",
    phone: "+27 81 833 6147",
  },
  trigger: {
    type: "insurance_quote_issued",
    source: "insurance_platform",
    reference: "Q-2026-00458",
  },
  product: {
    product_type: "life_insurance",
    product_name: "Term Life Cover — R2m sum assured",
    term_months: 240,
    monthly_premium: 486.50,
    sum_assured: 2000000,
    currency: "ZAR",
  },
  banker: {
    advisor_email: "advisor.demo@altara-preview.example.com",
    advisor_name: "James Botha",
    branch: "Sandton City",
    callback_url: "https://webhook.site/your-demo-endpoint",
  },
  documents: [
    { kind: "quote",          filename: "quote-Q-2026-00458.pdf",  url: "https://banker.example/dms/quote.pdf",         sha256: "a1b2c3", mime_type: "application/pdf" },
    { kind: "disclosure",     filename: "tcf-disclosure-v3.pdf",   url: "https://banker.example/dms/disclosure.pdf",    sha256: "d4e5f6", mime_type: "application/pdf" },
    { kind: "policy_wording", filename: "policy-wording-2026.pdf", url: "https://banker.example/dms/wording.pdf",       sha256: "7a8b9c", mime_type: "application/pdf" },
  ],
  compliance: { tcf_flag: true, cooling_off_days: 31, fsca_advice_provided: true },
}, null, 2);

export function SimulatePushSection({ onCreated }) {
  const [payload, setPayload] = useState(SAMPLE_PAYLOAD);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const simulate = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const body = JSON.parse(payload);
      const { data } = await api.post("/admin/banker/simulate-push", body);
      setResult(data);
      onCreated?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Simulation failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="border border-slate-200 rounded-sm bg-white p-5">
      <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold flex items-center gap-1.5">
        <Send className="w-3.5 h-3.5" /> Simulate a banker push
      </div>
      <div className="text-xs text-slate-600 mt-1 mb-3">
        Reproduces exactly what a banker&rsquo;s core system would <code className="text-[11px] bg-slate-50 border border-slate-200 px-1 rounded">POST</code> to <code className="text-[11px] bg-slate-50 border border-slate-200 px-1 rounded">/api/banker/journeys/create</code>. Bypasses the API-key check so you can demo the full round-trip without wiring a real bank.
      </div>
      <Textarea
        value={payload} onChange={e => setPayload(e.target.value)}
        className="font-mono text-[11px] rounded-sm h-72 resize-y"
        spellCheck={false}
        data-testid="simulate-push-payload"
      />
      <div className="mt-3 flex items-center gap-2">
        <Button onClick={simulate} disabled={busy} className="rounded-sm bg-altara-deep hover:bg-altara-ink" data-testid="simulate-push-btn">
          <Send className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Pushing…" : "Simulate push"}
        </Button>
        <Button variant="outline" onClick={() => setPayload(SAMPLE_PAYLOAD)} className="rounded-sm">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reset payload
        </Button>
      </div>

      {error && <div className="mt-3 text-xs text-rose-700 border border-rose-200 bg-rose-50/60 rounded-sm p-3">{String(error)}</div>}

      {result && (
        <div data-testid="simulate-push-result" className="mt-3 border border-emerald-200 bg-emerald-50/60 rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5" /> Journey created — email the consumer this link:
          </div>
          <div className="mt-2 flex items-center gap-2">
            <a href={result.journey_url} target="_blank" rel="noreferrer" className="flex-1 text-xs font-mono bg-white border border-emerald-200 rounded-sm px-3 py-2 truncate text-altara-deep hover:underline">
              {result.journey_url}
            </a>
            <Button size="sm" variant="outline" onClick={() => navigator.clipboard?.writeText(result.journey_url)} className="rounded-sm">
              <Copy className="w-3.5 h-3.5" />
            </Button>
            <a href={result.journey_url} target="_blank" rel="noreferrer">
              <Button size="sm" className="rounded-sm bg-altara-teal hover:bg-altara-deep">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open
              </Button>
            </a>
          </div>
          <div className="mt-2 text-[10px] font-mono text-slate-500">
            journey_id: {result.journey_id} · consumer_id: {result.consumer_id} · expires: {new Date(result.expires_at).toLocaleDateString()}
          </div>
        </div>
      )}
    </section>
  );
}

export function PushHistorySection({ history, onRefresh }) {
  return (
    <section className="border border-slate-200 rounded-sm bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Push history</div>
        <button onClick={onRefresh} className="text-[11px] text-slate-500 hover:text-altara-deep inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <div className="border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">When</th>
              <th className="text-left px-4 py-2 font-medium">Product</th>
              <th className="text-left px-4 py-2 font-medium">Ref</th>
              <th className="text-left px-4 py-2 font-medium">Advisor</th>
              <th className="text-left px-4 py-2 font-medium">Docs</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-left px-4 py-2 font-medium">Callback</th>
              <th className="text-left px-4 py-2 font-medium">Journey link</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No banker pushes yet. Simulate one above.</td></tr>}
            {history.map(h => (
              <tr key={h.journey_id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{new Date(h.created_at).toLocaleString()}</td>
                <td className="px-4 py-2 text-slate-800">{h.product_of_interest}</td>
                <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{h.banker_reference || "—"}</td>
                <td className="px-4 py-2 text-slate-600">{h.banker_advisor_email || "—"}</td>
                <td className="px-4 py-2 tabular-nums">{h.documents_count}</td>
                <td className="px-4 py-2">
                  <span className="text-[10px] uppercase tracking-widest bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-sm">{h.status}</span>
                </td>
                <td className="px-4 py-2">
                  {h.webhook_status
                    ? <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 rounded-sm ${
                        h.webhook_status === "delivered" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                        h.webhook_status === "failed"    ? "bg-rose-50 text-rose-800 border-rose-200" :
                                                           "bg-amber-50 text-amber-800 border-amber-200"
                      }`}>{h.webhook_status}</span>
                    : <span className="text-[10px] text-slate-400">—</span>}
                </td>
                <td className="px-4 py-2">
                  {h.journey_url ? (
                    <a href={h.journey_url} target="_blank" rel="noreferrer" className="text-[10px] text-altara-teal hover:underline inline-flex items-center gap-1">
                      open <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
